import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash, PencilSimple, FloppyDisk } from '@phosphor-icons/react';
import { API } from './adminConstants';

export function ServiceTypeManagement() {
  const { getAuthHeader } = useAuth();
  const [types, setTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editType, setEditType] = useState(null);
  const [form, setForm] = useState({ label: '', value: '', email: '', order: 0 });

  const load = async () => { try { const r = await axios.get(`${API}/admin/service-types`, { headers: getAuthHeader() }); setTypes(r.data); } catch { toast.error('Failed to load service types'); } };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ label: '', value: '', email: '', order: types.length }); setEditType(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.label || !form.email) { toast.error('Label and email required'); return; }
    const payload = { ...form, value: form.value || form.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') };
    try {
      if (editType) { await axios.put(`${API}/admin/service-types/${editType.id}`, payload, { headers: getAuthHeader() }); toast.success('Service type updated'); }
      else { await axios.post(`${API}/admin/service-types`, payload, { headers: getAuthHeader() }); toast.success('Service type created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (t) => { setEditType(t); setForm({ label: t.label || '', value: t.value || '', email: t.email || '', order: t.order || 0 }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/service-types/${id}`, { headers: getAuthHeader() }); toast.success('Service type deleted'); load(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid="service-type-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Contact Form Service Types</h1><p className="text-slate-500 text-sm">Manage the service type options shown in the website contact form and which email receives the enquiry</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-service-type-btn"><Plus size={15} className="mr-1" />Add Service Type</Button>
      </div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="service-type-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editType ? 'Edit Service Type' : 'New Service Type'}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">Display Label *</Label><Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Commercial Waste Collection" data-testid="service-type-label" /></div>
              <div><Label className="text-xs">Routing Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="e.g. waste@nsecltd.co.uk" data-testid="service-type-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">Slug / Value (auto-generated)</Label><Input value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder="commercial-waste" /></div>
              <div><Label className="text-xs">Display Order</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} min="0" /></div>
            </div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-service-type-btn"><FloppyDisk size={15} className="mr-1" />{editType ? 'Update' : 'Create'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Order</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Label</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Slug</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Routing Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {types.map(t => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`service-type-row-${t.id}`}>
                <td className="px-4 py-3 text-sm text-slate-500">{t.order}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{t.label}</td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono text-xs">{t.value}</td>
                <td className="px-4 py-3 text-sm text-slate-600">{t.email}</td>
                <td className="px-4 py-3"><div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(t)} className="h-7 w-7 p-0"><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash size={14} /></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {types.length === 0 && <div className="p-8 text-center text-slate-500">No service types configured</div>}
      </div>
    </div>
  );
}
